import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Bluetooth,
  Boxes,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  Layers,
  LayoutGrid,
  Palette,
  Percent,
  Plus,
  Printer,
  Receipt,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Truck,
  Type,
  Usb,
  UserPlus,
  Users,
  UtensilsCrossed,
  Wallet,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { printerManager, type HardwarePrinterSettings, type PrintPreference } from "@/lib/escpos/manager";
import {
  createBranch,
  createUser,
  getCurrentTenant,
  listBranches,
  listUsers,
  updateCurrentTenant,
  type BranchCreatePayload,
  type TenantUpdatePayload,
  type UserCreatePayload,
} from "@/lib/organization";
import { printTicket, printViaBrowser } from "@/lib/printing";
import { getMySubscription } from "@/lib/saas";
import { getSession, updateSessionTenant } from "@/lib/session";
import {
  applyTenantTheme,
  COLOR_PRESETS,
  DEFAULT_TENANT_SETTINGS,
  FONT_FAMILY_MAP,
  FONT_SCALE_MAP,
  getContrastForeground,
  getEffectiveTenantSettings,
  type FontFamilyKey,
  type FontScaleKey,
  type TenantSettings,
  type ThemeMode,
  type TicketPaperWidth,
} from "@/lib/theme";

export function BusinessAdminScreen() {
  const queryClient = useQueryClient();
  const session = getSession();

  const [activeTab, setActiveTab] = useState<
    "apariencia" | "ticket" | "negocio" | "servicios" | "usuarios" | "sucursales"
  >("apariencia");

  // Queries
  const { data: tenant } = useQuery({
    queryKey: ["organization", "tenant"],
    queryFn: getCurrentTenant,
  });

  const { data: subscription } = useQuery({
    queryKey: ["saas", "my-subscription"],
    queryFn: getMySubscription,
  });

  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["organization", "users"],
    queryFn: listUsers,
  });

  const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
    queryKey: ["organization", "branches"],
    queryFn: listBranches,
  });

  // Estado del formulario de Negocio y Operación
  const [tenantForm, setTenantForm] = useState<TenantUpdatePayload>({
    name: "",
    legal_name: "",
    rfc: "",
    tax_regime: "",
    fiscal_postal_code: "",
    timezone: "America/Mexico_City",
    currency: "MXN",
    prices_include_tax: true,
    business_day_cutoff_hour: 4,
    suggested_tip_percent: 10,
  });

  // Estado de Configuración (Apariencia + Ticket + Identidad Comercial)
  const [settings, setSettings] = useState<TenantSettings>(() =>
    getEffectiveTenantSettings(session?.tenant),
  );

  const [tenantSuccess, setTenantSuccess] = useState(false);

  // Estado de Impresora Física ESC/POS (WebUSB / Web Bluetooth)
  const [hardwareSettings, setHardwareSettings] = useState<HardwarePrinterSettings>(() =>
    printerManager.getSettings(),
  );
  const [usbConnected, setUsbConnected] = useState<boolean>(() => printerManager.isUsbConnected());
  const [btConnected, setBtConnected] = useState<boolean>(() => printerManager.isBluetoothConnected());
  const [usbDeviceInfo, setUsbDeviceInfo] = useState(() => printerManager.getUsbInfo());
  const [btDeviceInfo, setBtDeviceInfo] = useState(() => printerManager.getBluetoothInfo());
  const [hardwareConnecting, setHardwareConnecting] = useState(false);
  const [hardwareMessage, setHardwareMessage] = useState<{
    text: string;
    type: "success" | "error" | "info";
  } | null>(null);

  useEffect(() => {
    // Intentar reconectar silenciosamente dispositivos previamente emparejados
    printerManager.ensureConnection().then(() => {
      setUsbConnected(printerManager.isUsbConnected());
      setUsbDeviceInfo(printerManager.getUsbInfo());
      setBtConnected(printerManager.isBluetoothConnected());
      setBtDeviceInfo(printerManager.getBluetoothInfo());
    });
  }, []);

  const handleUpdateHardwareSettings = (partial: Partial<HardwarePrinterSettings>) => {
    const updated = printerManager.updateSettings(partial);
    setHardwareSettings(updated);
  };

  const handleConnectUsb = async () => {
    setHardwareConnecting(true);
    setHardwareMessage(null);
    try {
      const info = await printerManager.connectUsb();
      setUsbConnected(true);
      setUsbDeviceInfo(info);
      setHardwareSettings(printerManager.getSettings());
      setHardwareMessage({ text: `Impresora USB vinculada con éxito: ${info.productName}`, type: "success" });
    } catch (err: any) {
      if (err?.name !== "NotFoundError") {
        setHardwareMessage({ text: err?.message || "No se pudo vincular la impresora USB.", type: "error" });
      }
    } finally {
      setHardwareConnecting(false);
    }
  };

  const handleDisconnectUsb = async () => {
    try {
      await printerManager.disconnectUsb();
      setUsbConnected(false);
      setUsbDeviceInfo(null);
      setHardwareSettings(printerManager.getSettings());
      setHardwareMessage({ text: "Impresora USB desconectada.", type: "info" });
    } catch (err: any) {
      setHardwareMessage({ text: err?.message || "Error al desconectar USB.", type: "error" });
    }
  };

  const handleConnectBluetooth = async () => {
    setHardwareConnecting(true);
    setHardwareMessage(null);
    try {
      const info = await printerManager.connectBluetooth();
      setBtConnected(true);
      setBtDeviceInfo(info);
      setHardwareSettings(printerManager.getSettings());
      setHardwareMessage({ text: `Impresora Bluetooth vinculada: ${info.name}`, type: "success" });
    } catch (err: any) {
      if (err?.name !== "NotFoundError") {
        setHardwareMessage({ text: err?.message || "No se pudo vincular la impresora Bluetooth.", type: "error" });
      }
    } finally {
      setHardwareConnecting(false);
    }
  };

  const handleDisconnectBluetooth = async () => {
    try {
      await printerManager.disconnectBluetooth();
      setBtConnected(false);
      setBtDeviceInfo(null);
      setHardwareSettings(printerManager.getSettings());
      setHardwareMessage({ text: "Impresora Bluetooth desvinculada.", type: "info" });
    } catch (err: any) {
      setHardwareMessage({ text: err?.message || "Error al desconectar Bluetooth.", type: "error" });
    }
  };

  const handleDirectEscPosTest = async () => {
    setHardwareConnecting(true);
    setHardwareMessage(null);
    try {
      const width = settings.ticket_paper_width === "58mm" ? 58 : 80;
      const bName = tenantForm.name || session?.tenant?.name || "MegaSyR";
      const result = await printerManager.printTest(bName, width);
      if (result.success) {
        setHardwareMessage({
          text: `Ticket de calibración enviado directamente vía ${result.destination.toUpperCase()}.`,
          type: "success",
        });
      } else {
        setHardwareMessage({
          text: "No hay impresora USB o Bluetooth conectada para prueba física directa.",
          type: "info",
        });
      }
    } catch (err: any) {
      setHardwareMessage({ text: err?.message || "Error al imprimir ticket ESC/POS.", type: "error" });
    } finally {
      setHardwareConnecting(false);
    }
  };

  useEffect(() => {
    if (tenant) {
      setTenantForm({
        name: tenant.name ?? "",
        legal_name: tenant.legal_name ?? "",
        rfc: tenant.rfc ?? "",
        tax_regime: tenant.tax_regime ?? "",
        fiscal_postal_code: tenant.fiscal_postal_code ?? "",
        timezone: tenant.timezone ?? "America/Mexico_City",
        currency: tenant.currency ?? "MXN",
        prices_include_tax: tenant.prices_include_tax ?? true,
        business_day_cutoff_hour: tenant.business_day_cutoff_hour ?? 4,
        suggested_tip_percent: tenant.suggested_tip_percent ?? 10,
      });

      const effective = getEffectiveTenantSettings(tenant);
      setSettings(effective);
    }
  }, [tenant]);

  const tenantMutation = useMutation({
    mutationFn: (payload: TenantUpdatePayload) => updateCurrentTenant(payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["organization", "tenant"] });
      updateSessionTenant(updated);
      applyTenantTheme(updated.settings, updated.slug);
      setTenantSuccess(true);
      setTimeout(() => setTenantSuccess(false), 4000);
    },
  });

  function handleSaveAll() {
    tenantMutation.mutate({
      ...tenantForm,
      settings: settings as unknown as Record<string, unknown>,
    });
  }

  function handleThemeSettingChange<K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    applyTenantTheme(updated, session?.tenant?.slug);
  }

  function handleColorPresetSelect(presetId: string) {
    const found = COLOR_PRESETS.find((p) => p.id === presetId);
    if (!found) return;
    const updated: TenantSettings = {
      ...settings,
      primary_color: found.primary,
      secondary_color: found.secondary,
    };
    setSettings(updated);
    applyTenantTheme(updated, session?.tenant?.slug);
  }

  function handleResetDefaultTheme() {
    setSettings(DEFAULT_TENANT_SETTINGS);
    applyTenantTheme(DEFAULT_TENANT_SETTINGS, session?.tenant?.slug);
  }

  // Impresión de ticket de prueba
  async function handlePrintSampleTicket() {
    const dummySale = {
      id: "sample-sale",
      series: settings.default_sale_series || "A",
      folio: 101,
      business_date: new Date().toISOString().split("T")[0],
      created_at: new Date().toISOString(),
      subtotal_cents: 15000,
      discount_cents: 0,
      tax_cents: 2400,
      total_cents: 17400,
      change_cents: 2600,
      tip_cents: 1500,
      lines: [
        {
          id: "1",
          name: "Producto de Prueba Demo",
          quantity: 2,
          unit_price_cents: 6000,
          total_cents: 12000,
          discount_cents: 0,
          sku: "DEMO-01",
        },
        {
          id: "2",
          name: "Servicio Especial Mostrador",
          quantity: 1,
          unit_price_cents: 3000,
          total_cents: 3000,
          discount_cents: 0,
          sku: "SRV-02",
        },
      ],
      payments: [
        {
          id: "p1",
          method: "efectivo",
          amount_cents: 20000,
          payment_reference: null,
          created_at: new Date().toISOString(),
        },
      ],
    } as any;

    const result = await printTicket(dummySale, {
      businessName: tenantForm.name || session?.tenant?.name || "MegaSyR",
      branchName: session?.branches?.[0]?.name || "Matriz",
      cashierName: session?.user?.full_name || "Cajero Demo",
      paperWidth: settings.ticket_paper_width === "58mm" ? 58 : 80,
      slogan: settings.ticket_show_slogan ? settings.commercial_slogan : undefined,
      rfc: settings.ticket_show_rfc ? tenantForm.rfc : undefined,
      taxRegime: settings.ticket_show_regime ? tenantForm.tax_regime : undefined,
      address: settings.ticket_show_address ? (settings.contact_address || session?.branches?.[0]?.address) : undefined,
      phone: settings.ticket_show_phone ? (settings.contact_phone || session?.branches?.[0]?.phone) : undefined,
      showCashier: settings.ticket_show_cashier,
      showBranch: settings.ticket_show_branch,
      showFolio: settings.ticket_show_folio,
      showTaxBreakdown: settings.ticket_show_tax_breakdown,
      headerNote: settings.ticket_header_note,
      footerNote: settings.ticket_footer_note,
      billingInstructions: settings.ticket_billing_instructions,
    });

    if (result.destination === "usb") {
      setHardwareMessage({ text: "Ticket de prueba impreso directamente por USB.", type: "success" });
    } else if (result.destination === "bluetooth") {
      setHardwareMessage({ text: "Ticket de prueba impreso directamente por Bluetooth.", type: "success" });
    } else if (result.destination === "agent") {
      setHardwareMessage({ text: "Ticket de prueba enviado a través del agente local.", type: "success" });
    }
  }

  // Modales Usuario y Sucursal
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState<UserCreatePayload>({
    email: "",
    full_name: "",
    password: "",
    role_code: "cashier",
    pin: "",
  });

  const userMutation = useMutation({
    mutationFn: (payload: UserCreatePayload) => createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", "users"] });
      setShowUserModal(false);
      setUserForm({ email: "", full_name: "", password: "", role_code: "cashier", pin: "" });
    },
  });

  const [showBranchModal, setShowBranchModal] = useState(false);
  const [branchForm, setBranchForm] = useState<BranchCreatePayload>({
    name: "",
    code: "",
    address: "",
    phone: "",
    timezone: "America/Mexico_City",
  });

  const branchMutation = useMutation({
    mutationFn: (payload: BranchCreatePayload) => createBranch(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", "branches"] });
      setShowBranchModal(false);
      setBranchForm({ name: "", code: "", address: "", phone: "", timezone: "America/Mexico_City" });
    },
  });

  const hasPos = tenant?.has_pos ?? session?.tenant?.has_pos ?? true;
  const hasRestaurant = tenant?.has_restaurant ?? session?.tenant?.has_restaurant ?? false;
  const hasWorkshop = tenant?.has_workshop ?? session?.tenant?.has_workshop ?? false;

  return (
    <div className="bg-surface text-carbon min-h-[calc(100vh-4rem)] p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-extrabold text-carbon">
              Configuración de {tenant?.name || session?.tenant?.name || "Mi Negocio"}
            </h1>
            <StatusPill tone="ok">Activo</StatusPill>
          </div>
          <p className="mt-1 text-sm text-muted">
            Personaliza la identidad visual, colores, ticket térmico, datos fiscales y operación de tu sistema.
          </p>
        </div>

        {/* Pestañas de configuración */}
        <div className="flex flex-wrap gap-1.5 rounded-msr border border-line bg-snow p-1 shadow-sm">
          {[
            { id: "apariencia", label: "Apariencia y Colores", icon: Palette },
            { id: "ticket", label: "Diseño de Ticket", icon: Receipt },
            { id: "negocio", label: "Empresa y Fiscales", icon: Building2 },
            { id: "servicios", label: "Operación y Módulos", icon: Layers },
            { id: "usuarios", label: "Usuarios", icon: Users },
            { id: "sucursales", label: "Sucursales", icon: Boxes },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-1.5 rounded-msr px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeTab === tab.id
                    ? "bg-electric text-on-electric shadow-sm"
                    : "text-muted hover:text-carbon hover:bg-surface"
                }`}
              >
                <Icon className="size-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Alerta de guardado exitoso */}
      {tenantSuccess && (
        <div className="flex items-center gap-2 rounded-msr border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
          <CheckCircle2 className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>Configuración guardada y sincronizada correctamente en todos tus dispositivos.</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 1: APARIENCIA, COLORES Y TIPOGRAFÍA                               */}
      {/* ========================================================================= */}
      {activeTab === "apariencia" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Controles de apariencia */}
          <div className="space-y-6 lg:col-span-7">
            {/* Presets de Color */}
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-carbon">Paleta de Color Principal</h3>
                  <p className="text-xs text-muted">Selecciona un tema prediseñado o ingresa tu propio código hexadecimal.</p>
                </div>
                <Button size="sm" variant="ghost" onClick={handleResetDefaultTheme} icon={<RotateCcw className="size-3.5" />}>
                  Restablecer
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {COLOR_PRESETS.map((preset) => {
                  const isSelected = settings.primary_color.toUpperCase() === preset.primary.toUpperCase();
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => handleColorPresetSelect(preset.id)}
                      className={`flex flex-col items-start rounded-xl border p-3 text-left transition-all ${
                        isSelected
                          ? "border-electric ring-2 ring-electric/30 bg-electric/5"
                          : "border-line bg-surface hover:border-line/80"
                      }`}
                    >
                      <div className="flex w-full items-center justify-between mb-2">
                        <span className="size-6 rounded-full shadow-inner" style={{ backgroundColor: preset.primary }} />
                        {isSelected && <CheckCircle2 className="size-4 text-electric" />}
                      </div>
                      <span className="text-xs font-bold text-carbon line-clamp-1">{preset.name}</span>
                      <span className="text-[10px] text-muted line-clamp-1">{preset.description}</span>
                    </button>
                  );
                })}
              </div>

              {/* Selector personalizado HEX */}
              <div className="border-t border-line pt-4 space-y-3">
                <label className="text-xs font-bold text-carbon">Color Primario Personalizado (HEX)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => handleThemeSettingChange("primary_color", e.target.value)}
                    className="size-11 cursor-pointer rounded-msr border border-line bg-snow p-1"
                  />
                  <input
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => handleThemeSettingChange("primary_color", e.target.value)}
                    placeholder="#007BFF"
                    className="h-10 w-36 rounded-msr border border-line bg-snow px-3 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                  />
                  <div className="flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-xs text-muted">
                    <Sparkles className="size-3.5 text-electric" />
                    <span>
                      Texto botón:{" "}
                      <strong className="text-carbon">
                        {getContrastForeground(settings.primary_color) === "#FFFFFF" ? "Blanco" : "Oscuro"}
                      </strong>{" "}
                      (WCAG OK)
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Modo de Interfaz */}
            <Card className="p-5 space-y-4">
              <div>
                <h3 className="text-base font-bold text-carbon">Modo Visual de la Interfaz</h3>
                <p className="text-xs text-muted">Ajusta el contraste para ambientes con mucha luz de día o turnos nocturnos.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleThemeSettingChange("theme_mode", "light")}
                  className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                    settings.theme_mode === "light"
                      ? "border-electric ring-2 ring-electric/30 bg-electric/5"
                      : "border-line bg-surface hover:border-line/80"
                  }`}
                >
                  <div className="size-8 rounded-full bg-white border border-line grid place-items-center text-amber-500 shadow-sm">
                    ☀️
                  </div>
                  <div>
                    <p className="text-sm font-bold text-carbon">Modo Claro (Estándar)</p>
                    <p className="text-xs text-muted">Fondos claros, contraste diurno óptimo.</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleThemeSettingChange("theme_mode", "dark")}
                  className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                    settings.theme_mode === "dark"
                      ? "border-electric ring-2 ring-electric/30 bg-electric/5"
                      : "border-line bg-surface hover:border-line/80"
                  }`}
                >
                  <div className="size-8 rounded-full bg-stone-900 border border-stone-700 grid place-items-center text-indigo-300 shadow-sm">
                    🌙
                  </div>
                  <div>
                    <p className="text-sm font-bold text-carbon">Modo Oscuro</p>
                    <p className="text-xs text-muted">Descanso visual, ideal para bares y noche.</p>
                  </div>
                </button>
              </div>
            </Card>

            {/* Tipografía y Escala */}
            <Card className="p-5 space-y-4">
              <div>
                <h3 className="text-base font-bold text-carbon">Tipografía y Escala de Pantalla</h3>
                <p className="text-xs text-muted">Selecciona la fuente que mejor refleje la personalidad de tu negocio.</p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-muted">Familia Tipográfica</label>
                  <select
                    value={settings.font_family}
                    onChange={(e) => handleThemeSettingChange("font_family", e.target.value as FontFamilyKey)}
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    {Object.entries(FONT_FAMILY_MAP).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Tamaño de Elementos Táctiles</label>
                  <select
                    value={settings.font_scale}
                    onChange={(e) => handleThemeSettingChange("font_scale", e.target.value as FontScaleKey)}
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    {Object.entries(FONT_SCALE_MAP).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-3 pt-2">
              <Button size="lg" onClick={handleSaveAll} loading={tenantMutation.isPending} icon={<CheckCircle2 className="size-4" />}>
                Guardar y Aplicar Cambios
              </Button>
            </div>
          </div>

          {/* Previsualización en Vivo de Botones y Componentes */}
          <div className="space-y-6 lg:col-span-5">
            <Card className="p-5 space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted">Vista Previa de Componentes en Vivo</h3>
              <p className="text-xs text-muted">
                Así es como se verán tus botones, tarjetas y elementos en el sistema con el color seleccionado:
              </p>

              <div className="rounded-xl border border-line bg-surface p-4 space-y-3">
                <Button fullWidth size="lg">
                  Botón Primario de Cobro ($1,450.00 MXN)
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" className="flex-1">
                    Secundario
                  </Button>
                  <Button variant="ghost" className="flex-1">
                    Acción Neutra
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-line bg-snow p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted">Estado del Sistema</span>
                  <StatusPill tone="ok">Operando Normal</StatusPill>
                </div>
                <p className="text-sm font-bold text-carbon">Resumen de Ventas de Hoy</p>
                <p className="font-mono text-2xl font-black text-carbon">$12,850.00</p>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 2: DISEÑO Y PERSONALIZACIÓN DE TICKET TÉRMICO                     */}
      {/* ========================================================================= */}
      {activeTab === "ticket" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Controles del Ticket */}
          <div className="space-y-6 lg:col-span-7">
            <Card className="p-5 space-y-4">
              <div>
                <h3 className="text-base font-bold text-carbon">Formato y Papel Térmico</h3>
                <p className="text-xs text-muted">Elige el ancho de bobina según tu impresora térmica.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleThemeSettingChange("ticket_paper_width", "80mm")}
                  className={`flex flex-col items-center justify-center rounded-xl border p-4 text-center transition-all ${
                    settings.ticket_paper_width === "80mm"
                      ? "border-electric ring-2 ring-electric/30 bg-electric/5"
                      : "border-line bg-surface hover:border-line/80"
                  }`}
                >
                  <Printer className="size-6 text-electric mb-1" />
                  <span className="text-sm font-bold text-carbon">80 mm (Estándar)</span>
                  <span className="text-xs text-muted">Impresoras Epson, Star, Bixolon</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleThemeSettingChange("ticket_paper_width", "58mm")}
                  className={`flex flex-col items-center justify-center rounded-xl border p-4 text-center transition-all ${
                    settings.ticket_paper_width === "58mm"
                      ? "border-electric ring-2 ring-electric/30 bg-electric/5"
                      : "border-line bg-surface hover:border-line/80"
                  }`}
                >
                  <Printer className="size-6 text-electric mb-1" />
                  <span className="text-sm font-bold text-carbon">58 mm (Compacto)</span>
                  <span className="text-xs text-muted">Terminales móviles y portátiles</span>
                </button>
              </div>

              {/* Impresión Automática */}
              <div className="flex items-center justify-between border-t border-line pt-4">
                <div>
                  <p className="text-sm font-bold text-carbon">Impresión Automática al Cobrar</p>
                  <p className="text-xs text-muted">Envía el ticket a la impresora de inmediato al completar cada venta.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.ticket_auto_print}
                    onChange={(e) => handleThemeSettingChange("ticket_auto_print", e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-line peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-line after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-electric"></div>
                </label>
              </div>
            </Card>

            {/* Elementos a mostrar en el ticket */}
            <Card className="p-5 space-y-3">
              <h3 className="text-base font-bold text-carbon">Elementos Visibles en el Ticket</h3>
              <p className="text-xs text-muted">Activa o desactiva qué datos se imprimen en cada comprobante.</p>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 pt-2">
                {[
                  { key: "ticket_show_slogan", label: "Eslogan comercial" },
                  { key: "ticket_show_rfc", label: "RFC del negocio" },
                  { key: "ticket_show_regime", label: "Régimen Fiscal SAT" },
                  { key: "ticket_show_address", label: "Dirección del establecimiento" },
                  { key: "ticket_show_phone", label: "Teléfono de contacto" },
                  { key: "ticket_show_cashier", label: "Nombre del cajero en turno" },
                  { key: "ticket_show_branch", label: "Nombre de la sucursal" },
                  { key: "ticket_show_folio", label: "Serie y Folio de venta" },
                  { key: "ticket_show_tax_breakdown", label: "Desglose de IVA" },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2 text-xs font-semibold text-carbon cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(settings[item.key as keyof TenantSettings])}
                      onChange={(e) => handleThemeSettingChange(item.key as keyof TenantSettings, e.target.checked as any)}
                      className="size-4 rounded border-line text-electric focus:ring-electric"
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </Card>

            {/* Textos y Notas del Ticket */}
            <Card className="p-5 space-y-4">
              <h3 className="text-base font-bold text-carbon">Mensajes Personalizados y Facturación</h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-muted">Mensaje de Cabecera / Bienvenida</label>
                  <input
                    type="text"
                    value={settings.ticket_header_note}
                    onChange={(e) => handleThemeSettingChange("ticket_header_note", e.target.value)}
                    placeholder="¡Bienvenido a nuestro establecimiento!"
                    className="mt-1 h-9 w-full rounded-msr border border-line bg-snow px-3 text-xs text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Mensaje de Pie de Ticket / Despedida</label>
                  <input
                    type="text"
                    value={settings.ticket_footer_note}
                    onChange={(e) => handleThemeSettingChange("ticket_footer_note", e.target.value)}
                    placeholder="¡Gracias por su compra! Conserve este ticket para aclaraciones."
                    className="mt-1 h-9 w-full rounded-msr border border-line bg-snow px-3 text-xs text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Instrucciones de Facturación</label>
                  <input
                    type="text"
                    value={settings.ticket_billing_instructions}
                    onChange={(e) => handleThemeSettingChange("ticket_billing_instructions", e.target.value)}
                    placeholder="Solicite su factura dentro del mes en curso en facturacion@minegocio.mx"
                    className="mt-1 h-9 w-full rounded-msr border border-line bg-snow px-3 text-xs text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>
            </Card>

            {/* Tarjeta de Hardware de Impresión Directa ESC/POS */}
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-line pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-electric/10 text-electric">
                    <Zap className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-carbon">Hardware de Impresión Directa ESC/POS</h3>
                    <p className="text-xs text-muted">
                      Imprime a 1 clic en rollos térmicos sin abrir el diálogo del navegador.
                    </p>
                  </div>
                </div>
                <StatusPill
                  variant={usbConnected || btConnected ? "emerald" : "neutral"}
                  label={usbConnected ? "USB Conectado" : btConnected ? "Bluetooth Conectado" : "Modo Navegador"}
                />
              </div>

              {hardwareMessage && (
                <div
                  className={`rounded-msr p-3 text-xs flex items-center justify-between ${
                    hardwareMessage.type === "success"
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                      : hardwareMessage.type === "error"
                      ? "bg-rose-500/10 text-rose-400 border border-rose-500/30"
                      : "bg-electric/10 text-electric border border-electric/30"
                  }`}
                >
                  <span>{hardwareMessage.text}</span>
                  <button
                    onClick={() => setHardwareMessage(null)}
                    className="ml-2 text-muted hover:text-carbon"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {/* Dispositivo WebUSB */}
                <div className="rounded-msr border border-line bg-snow/50 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Usb className="size-4 text-electric" />
                      <span className="text-xs font-semibold text-carbon">Impresora USB</span>
                    </div>
                    {usbConnected ? (
                      <span className="text-[10px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                        Activa
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted">No vinculada</span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted truncate">
                    {usbDeviceInfo
                      ? usbDeviceInfo.productName
                      : hardwareSettings.lastUsbDevice
                      ? `${hardwareSettings.lastUsbDevice.name} (desconectada)`
                      : "Epson, Xprinter, POS-58/80, Rongta, etc."}
                  </p>
                  <div className="pt-1">
                    {usbConnected ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleDisconnectUsb}
                        disabled={hardwareConnecting}
                        className="w-full text-xs"
                      >
                        Desconectar USB
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleConnectUsb}
                        disabled={hardwareConnecting || !printerManager.isUsbSupported()}
                        className="w-full text-xs"
                        icon={<Usb className="size-3.5" />}
                      >
                        {printerManager.isUsbSupported() ? "Vincular USB" : "USB no disponible"}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Dispositivo Web Bluetooth */}
                <div className="rounded-msr border border-line bg-snow/50 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bluetooth className="size-4 text-sky-400" />
                      <span className="text-xs font-semibold text-carbon">Impresora Bluetooth</span>
                    </div>
                    {btConnected ? (
                      <span className="text-[10px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                        Activa
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted">No vinculada</span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted truncate">
                    {btDeviceInfo
                      ? btDeviceInfo.name
                      : hardwareSettings.lastBluetoothDevice
                      ? `${hardwareSettings.lastBluetoothDevice.name} (desconectada)`
                      : "Impresoras portátiles BLE (58mm/80mm)"}
                  </p>
                  <div className="pt-1">
                    {btConnected ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleDisconnectBluetooth}
                        disabled={hardwareConnecting}
                        className="w-full text-xs"
                      >
                        Desconectar Bluetooth
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleConnectBluetooth}
                        disabled={hardwareConnecting || !printerManager.isBluetoothSupported()}
                        className="w-full text-xs"
                        icon={<Bluetooth className="size-3.5" />}
                      >
                        {printerManager.isBluetoothSupported() ? "Vincular Bluetooth" : "BLE no disponible"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Ajustes de Hardware */}
              <div className="space-y-3 border-t border-line pt-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-muted">Preferencia de Destino</label>
                    <select
                      value={hardwareSettings.preference}
                      onChange={(e) =>
                        handleUpdateHardwareSettings({ preference: e.target.value as PrintPreference })
                      }
                      className="mt-1 h-9 w-full rounded-msr border border-line bg-snow px-3 text-xs text-carbon focus:border-electric focus:outline-none"
                    >
                      <option value="auto">Automático (USB → BLE → Agente → Diálogo)</option>
                      <option value="usb">Forzar WebUSB</option>
                      <option value="bluetooth">Forzar Bluetooth</option>
                      <option value="agent">Forzar Agente Local (localhost)</option>
                      <option value="browser">Solo Diálogo Navegador (PDF/iframe)</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2 pt-5">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleDirectEscPosTest}
                      disabled={hardwareConnecting || (!usbConnected && !btConnected)}
                      className="w-full text-xs"
                      icon={<Printer className="size-3.5" />}
                    >
                      Probar Hardware ESC/POS
                    </Button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-carbon">
                    <input
                      type="checkbox"
                      checked={hardwareSettings.openDrawerOnCash}
                      onChange={(e) => handleUpdateHardwareSettings({ openDrawerOnCash: e.target.checked })}
                      className="size-4 rounded border-line text-electric focus:ring-0"
                    />
                    <span>Apertura de cajón (RJ11) con efectivo</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs text-carbon">
                    <input
                      type="checkbox"
                      checked={hardwareSettings.autoCut}
                      onChange={(e) => handleUpdateHardwareSettings({ autoCut: e.target.checked })}
                      className="size-4 rounded border-line text-electric focus:ring-0"
                    />
                    <span>Corte automático de papel tras imprimir</span>
                  </label>
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-3 pt-2">
              <Button size="lg" onClick={handleSaveAll} loading={tenantMutation.isPending} icon={<CheckCircle2 className="size-4" />}>
                Guardar Configuración de Ticket
              </Button>
            </div>
          </div>

          {/* Vista Previa en Vivo del Ticket Térmico */}
          <div className="space-y-4 lg:col-span-5">
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted">Vista Previa de Ticket</h3>
                  <p className="text-xs text-muted">Formato monoespaciado real para papel de {settings.ticket_paper_width}.</p>
                </div>
                <Button size="sm" variant="secondary" onClick={handlePrintSampleTicket} icon={<Printer className="size-3.5" />}>
                  Imprimir Prueba
                </Button>
              </div>

              {/* Simulación física de rollo térmico */}
              <div
                className={`mx-auto bg-white p-5 text-zinc-900 shadow-xl border border-zinc-200 rounded-sm font-mono text-[11px] leading-relaxed select-none ${
                  settings.ticket_paper_width === "58mm" ? "max-w-[260px]" : "max-w-[340px]"
                }`}
              >
                <div className="text-center space-y-0.5">
                  <p className="text-sm font-black uppercase tracking-wider">
                    {tenantForm.name || session?.tenant?.name || "MEGASYR"}
                  </p>
                  {settings.ticket_show_slogan && settings.commercial_slogan && (
                    <p className="text-[10px] text-zinc-600 italic">{settings.commercial_slogan}</p>
                  )}
                  {settings.ticket_show_rfc && tenantForm.rfc && (
                    <p className="text-[10px]">RFC: {tenantForm.rfc}</p>
                  )}
                  {settings.ticket_show_regime && tenantForm.tax_regime && (
                    <p className="text-[10px]">Régimen: {tenantForm.tax_regime}</p>
                  )}
                  {settings.ticket_show_address && (settings.contact_address || session?.branches?.[0]?.address) && (
                    <p className="text-[10px] text-zinc-600">
                      {settings.contact_address || session?.branches?.[0]?.address}
                    </p>
                  )}
                  {settings.ticket_show_phone && (settings.contact_phone || session?.branches?.[0]?.phone) && (
                    <p className="text-[10px]">Tel: {settings.contact_phone || session?.branches?.[0]?.phone}</p>
                  )}
                  {settings.ticket_show_branch && (
                    <p className="text-[10px] font-bold">Sucursal: {session?.branches?.[0]?.name || "Matriz"}</p>
                  )}
                </div>

                {settings.ticket_header_note && (
                  <div className="my-2 border-t border-dashed border-zinc-400 pt-1 text-center text-[10px]">
                    {settings.ticket_header_note}
                  </div>
                )}

                <div className="my-2 border-t border-dashed border-zinc-400 pt-1">
                  {settings.ticket_show_folio && (
                    <div className="flex justify-between">
                      <span>Ticket #{settings.default_sale_series}-101</span>
                      <span>{new Date().toISOString().split("T")[0]}</span>
                    </div>
                  )}
                  {settings.ticket_show_cashier && (
                    <div className="flex justify-between text-zinc-600">
                      <span>Cajero:</span>
                      <span>{session?.user?.full_name || "Cajero 1"}</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-dashed border-zinc-400 py-1.5 space-y-1">
                  <div className="flex justify-between">
                    <span>2 x Producto Demo</span>
                    <span>$120.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>1 x Servicio Especial</span>
                    <span>$30.00</span>
                  </div>
                </div>

                <div className="border-t border-dashed border-zinc-400 pt-1.5 space-y-0.5">
                  <div className="flex justify-between text-zinc-600">
                    <span>Subtotal</span>
                    <span>$150.00</span>
                  </div>
                  {settings.ticket_show_tax_breakdown && (
                    <div className="flex justify-between text-zinc-600">
                      <span>IVA (16%)</span>
                      <span>$24.00</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs font-black pt-1 border-t border-zinc-800">
                    <span>TOTAL</span>
                    <span>$174.00</span>
                  </div>
                  <div className="flex justify-between text-zinc-600 pt-1">
                    <span>Efectivo</span>
                    <span>$200.00</span>
                  </div>
                  <div className="flex justify-between text-zinc-600">
                    <span>Cambio</span>
                    <span>$26.00</span>
                  </div>
                </div>

                {settings.ticket_footer_note && (
                  <div className="mt-3 border-t border-dashed border-zinc-400 pt-2 text-center text-[10px] text-zinc-600">
                    {settings.ticket_footer_note}
                  </div>
                )}

                {settings.ticket_billing_instructions && (
                  <div className="mt-2 text-center text-[9px] text-zinc-500">
                    {settings.ticket_billing_instructions}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 3: EMPRESA Y DATOS FISCALES                                       */}
      {/* ========================================================================= */}
      {activeTab === "negocio" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <Card className="p-5 space-y-4">
              <h3 className="text-base font-bold text-carbon">Identidad Comercial y Contacto</h3>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-muted">Nombre Comercial *</label>
                  <input
                    type="text"
                    value={tenantForm.name || ""}
                    onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Eslogan Comercial</label>
                  <input
                    type="text"
                    value={settings.commercial_slogan}
                    onChange={(e) => handleThemeSettingChange("commercial_slogan", e.target.value)}
                    placeholder="Calidad y servicio en cada venta"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Teléfono de Atención</label>
                  <input
                    type="tel"
                    value={settings.contact_phone}
                    onChange={(e) => handleThemeSettingChange("contact_phone", e.target.value)}
                    placeholder="867 213 1335"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Correo Electrónico de Contacto</label>
                  <input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => handleThemeSettingChange("contact_email", e.target.value)}
                    placeholder="contacto@minegocio.mx"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-muted">Dirección Comercial Completa</label>
                  <input
                    type="text"
                    value={settings.contact_address}
                    onChange={(e) => handleThemeSettingChange("contact_address", e.target.value)}
                    placeholder="Av. Hidalgo #123, Col. Centro, CP 95220, Veracruz, México"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-5 space-y-4">
              <h3 className="text-base font-bold text-carbon">Datos Fiscales (CFDI 4.0 SAT)</h3>
              <p className="text-xs text-muted">
                Requeridos para facturación electrónica conforme a la Constancia de Situación Fiscal.
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-muted">Razón Social Exacta *</label>
                  <input
                    type="text"
                    value={tenantForm.legal_name || ""}
                    onChange={(e) => setTenantForm({ ...tenantForm, legal_name: e.target.value })}
                    placeholder="DISTRIBUIDORA Y SERVICIOS INTEGRALES SA DE CV"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">RFC con Homoclave *</label>
                  <input
                    type="text"
                    maxLength={13}
                    value={tenantForm.rfc || ""}
                    onChange={(e) => setTenantForm({ ...tenantForm, rfc: e.target.value.toUpperCase() })}
                    placeholder="XAXX010101000"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-muted">Código Postal Fiscal *</label>
                  <input
                    type="text"
                    maxLength={5}
                    value={tenantForm.fiscal_postal_code || ""}
                    onChange={(e) => setTenantForm({ ...tenantForm, fiscal_postal_code: e.target.value })}
                    placeholder="95220"
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 font-mono text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-muted">Régimen Fiscal (SAT)</label>
                  <select
                    value={tenantForm.tax_regime || "612"}
                    onChange={(e) => setTenantForm({ ...tenantForm, tax_regime: e.target.value })}
                    className="mt-1 h-10 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    <option value="601">601 - General de Ley Personas Morales</option>
                    <option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</option>
                    <option value="626">626 - Régimen Simplificado de Confianza (RESICO)</option>
                    <option value="621">621 - Incorporación Fiscal (RIF)</option>
                    <option value="605">605 - Sueldos y Salarios e Ingresos Asimilados</option>
                  </select>
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-3 pt-2">
              <Button size="lg" onClick={handleSaveAll} loading={tenantMutation.isPending} icon={<CheckCircle2 className="size-4" />}>
                Guardar Datos Fiscales y de Empresa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 4: OPERACIÓN Y SERVICIOS                                         */}
      {/* ========================================================================= */}
      {activeTab === "servicios" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <Card className="p-5 space-y-4">
              <h3 className="text-base font-bold text-carbon">Preferencias de Operación en Mostrador</h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded-xl border border-line bg-surface p-3">
                  <div>
                    <p className="text-sm font-bold text-carbon">Precios con Impuesto Incluido</p>
                    <p className="text-xs text-muted">El precio de etiqueta ya incluye el IVA (16%).</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={tenantForm.prices_include_tax ?? true}
                    onChange={(e) => setTenantForm({ ...tenantForm, prices_include_tax: e.target.checked })}
                    className="size-5 rounded border-line text-electric focus:ring-electric cursor-pointer"
                  />
                </div>

                <div className="rounded-xl border border-line bg-surface p-3 space-y-1">
                  <label className="text-xs font-bold text-carbon">Serie por Defecto para Ventas</label>
                  <input
                    type="text"
                    maxLength={5}
                    value={settings.default_sale_series}
                    onChange={(e) => handleThemeSettingChange("default_sale_series", e.target.value.toUpperCase())}
                    className="h-9 w-full rounded-msr border border-line bg-snow px-3 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div className="rounded-xl border border-line bg-surface p-3 space-y-1">
                  <label className="text-xs font-bold text-carbon">Hora de Corte de Día Operativo</label>
                  <p className="text-[11px] text-muted">Hora en que cierran turnos y cortes nocturnos (0..23h).</p>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={tenantForm.business_day_cutoff_hour ?? 4}
                    onChange={(e) => setTenantForm({ ...tenantForm, business_day_cutoff_hour: Number(e.target.value) })}
                    className="h-9 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div className="rounded-xl border border-line bg-surface p-3 space-y-1">
                  <label className="text-xs font-bold text-carbon">Porcentaje de Propina Sugerida</label>
                  <p className="text-[11px] text-muted">Sugerida en restaurantes y servicio a mesa (%).</p>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={tenantForm.suggested_tip_percent ?? 10}
                    onChange={(e) => setTenantForm({ ...tenantForm, suggested_tip_percent: Number(e.target.value) })}
                    className="h-9 w-full rounded-msr border border-line bg-snow px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button size="md" onClick={handleSaveAll} loading={tenantMutation.isPending} icon={<CheckCircle2 className="size-4" />}>
                  Guardar Preferencias Operativas
                </Button>
              </div>
            </Card>

            {/* Módulos Contratados */}
            <Card className="p-5 space-y-4">
              <h3 className="text-base font-bold text-carbon">Módulos Habilitados</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className={`rounded-xl border p-4 ${hasPos ? "border-emerald-500/30 bg-emerald-500/5" : "border-line bg-surface"}`}>
                  <LayoutGrid className={`size-5 mb-2 ${hasPos ? "text-emerald-600" : "text-muted"}`} />
                  <p className="text-sm font-bold text-carbon">Punto de Venta</p>
                  <p className="text-xs text-muted">{hasPos ? "Habilitado" : "No contratado"}</p>
                </div>

                <div className={`rounded-xl border p-4 ${hasRestaurant ? "border-emerald-500/30 bg-emerald-500/5" : "border-line bg-surface"}`}>
                  <UtensilsCrossed className={`size-5 mb-2 ${hasRestaurant ? "text-emerald-600" : "text-muted"}`} />
                  <p className="text-sm font-bold text-carbon">Restaurante y KDS</p>
                  <p className="text-xs text-muted">{hasRestaurant ? "Habilitado" : "No contratado"}</p>
                </div>

                <div className={`rounded-xl border p-4 ${hasWorkshop ? "border-emerald-500/30 bg-emerald-500/5" : "border-line bg-surface"}`}>
                  <Wrench className={`size-5 mb-2 ${hasWorkshop ? "text-emerald-600" : "text-muted"}`} />
                  <p className="text-sm font-bold text-carbon">Taller Mecánico</p>
                  <p className="text-xs text-muted">{hasWorkshop ? "Habilitado" : "No contratado"}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Columna Lateral: Plan SaaS Contratado */}
          <div className="space-y-6 lg:col-span-4">
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-carbon">Plan Contratado</h3>
                <StatusPill tone={subscription?.status === "active" ? "ok" : "warning"}>
                  {subscription?.status === "active" ? "Activo" : "Prueba"}
                </StatusPill>
              </div>

              <div className="rounded-xl border border-line bg-surface p-4 space-y-2">
                <p className="text-xs font-semibold text-muted uppercase tracking-wider">Plan Actual</p>
                <p className="text-lg font-extrabold text-carbon">
                  {subscription?.plan?.name ? `Plan ${subscription.plan.name}` : "Plan MegaSyR"}
                </p>
                {subscription?.plan?.description && (
                  <p className="text-xs text-muted">{subscription.plan.description}</p>
                )}
                {subscription?.plan?.price_monthly_cents ? (
                  <p className="text-sm font-bold text-electric">
                    ${(subscription.plan.price_monthly_cents / 100).toFixed(2)} MXN / mes
                  </p>
                ) : null}
              </div>

              <div className="rounded-xl border border-line bg-surface p-3 space-y-1 text-xs">
                <span className="text-muted font-semibold uppercase tracking-wider text-[10px]">Arquitectura de Datos</span>
                {tenant?.schema_mode === "dedicated" ? (
                  <div>
                    <p className="font-bold text-electric">Opción B · Esquema PostgreSQL Dedicado</p>
                    <p className="font-mono text-[11px] text-muted">{tenant.schema_name}</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-bold text-carbon">Opción A · Aislamiento Lógico por Fila</p>
                    <p className="text-[11px] text-muted">Prueba gratuita (30 días)</p>
                  </div>
                )}
              </div>

              {subscription?.usage && (
                <div className="space-y-3 pt-2">
                  <h4 className="text-xs font-bold text-muted uppercase tracking-wider">Uso de Límites</h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted">Sucursales</span>
                      <span className="font-semibold text-carbon">
                        {subscription.usage.branches_used} / {subscription.usage.branches_limit}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted">Usuarios</span>
                      <span className="font-semibold text-carbon">
                        {subscription.usage.users_used} / {subscription.usage.users_limit}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted">Productos</span>
                      <span className="font-semibold text-carbon">
                        {subscription.usage.products_used} / {subscription.usage.products_limit}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 5: USUARIOS                                                       */}
      {/* ========================================================================= */}
      {activeTab === "usuarios" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-carbon">Usuarios del Sistema</h3>
            <Button size="sm" onClick={() => setShowUserModal(true)} icon={<UserPlus className="size-4" />}>
              Nuevo Usuario
            </Button>
          </div>

          <Card className="overflow-hidden">
            {isLoadingUsers ? (
              <div className="p-8 text-center text-muted">Cargando usuarios...</div>
            ) : (
              <div className="divide-y divide-line">
                {(usersData?.items || []).map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-4 hover:bg-surface/50">
                    <div>
                      <p className="font-bold text-carbon">{u.full_name}</p>
                      <p className="text-xs text-muted">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-xs font-semibold text-carbon capitalize">
                        {u.role_code || "cajero"}
                      </span>
                      <StatusPill tone={u.is_active ? "ok" : "neutral"}>
                        {u.is_active ? "Activo" : "Inactivo"}
                      </StatusPill>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 6: SUCURSALES                                                     */}
      {/* ========================================================================= */}
      {activeTab === "sucursales" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-carbon">Sucursales del Negocio</h3>
            <Button size="sm" onClick={() => setShowBranchModal(true)} icon={<Plus className="size-4" />}>
              Nueva Sucursal
            </Button>
          </div>

          <Card className="overflow-hidden">
            {isLoadingBranches ? (
              <div className="p-8 text-center text-muted">Cargando sucursales...</div>
            ) : (
              <div className="divide-y divide-line">
                {(branchesData?.items || []).map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-4 hover:bg-surface/50">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-electric">[{b.code}]</span>
                        <p className="font-bold text-carbon">{b.name}</p>
                      </div>
                      <p className="text-xs text-muted">{b.address || "Sin dirección registrada"}</p>
                    </div>
                    <StatusPill tone={b.is_active ? "ok" : "neutral"}>
                      {b.is_active ? "Activa" : "Inactiva"}
                    </StatusPill>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Modal: Crear Usuario */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md space-y-4 p-6 shadow-2xl">
            <CardHeader title="Nuevo Usuario" description="Crear acceso para un empleado de tu negocio" />
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted">Nombre Completo *</label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  placeholder="Ej. Juan Pérez"
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Correo Electrónico *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Contraseña Temporal *</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Rol en el Sistema *</label>
                <select
                  value={userForm.role_code}
                  onChange={(e) => setUserForm({ ...userForm, role_code: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                >
                  <option value="cashier">Cajero / Mostrador</option>
                  <option value="waiter">Mesero (Restaurante)</option>
                  <option value="cook">Cocinero (KDS Cocina)</option>
                  <option value="mechanic">Mecánico (Taller)</option>
                  <option value="manager">Encargado de Sucursal</option>
                  <option value="admin">Administrador del Negocio</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowUserModal(false)}>
                Cancelar
              </Button>
              <Button onClick={() => userMutation.mutate(userForm)} disabled={userMutation.isPending || !userForm.email || !userForm.full_name}>
                Crear Usuario
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Crear Sucursal */}
      {showBranchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md space-y-4 p-6 shadow-2xl">
            <CardHeader title="Nueva Sucursal" description="Añade un punto de venta o almacén físico" />
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted">Código de Sucursal * (Ej. CTR, SUC-2)</label>
                <input
                  type="text"
                  maxLength={10}
                  value={branchForm.code}
                  onChange={(e) => setBranchForm({ ...branchForm, code: e.target.value.toUpperCase() })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Nombre de la Sucursal *</label>
                <input
                  type="text"
                  value={branchForm.name}
                  onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Dirección</label>
                <input
                  type="text"
                  value={branchForm.address || ""}
                  onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted">Teléfono</label>
                <input
                  type="tel"
                  value={branchForm.phone || ""}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  className="mt-1 h-10 w-full rounded-msr border border-line bg-surface px-3 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowBranchModal(false)}>
                Cancelar
              </Button>
              <Button onClick={() => branchMutation.mutate(branchForm)} disabled={branchMutation.isPending || !branchForm.code || !branchForm.name}>
                Crear Sucursal
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
